// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo, useCallback } from "react";
import IntlMessages from "Util/IntlMessages";
import { Row, Card, CardBody, CardTitle, Badge, Button } from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import { PERFECT_SCROLLBAR_MAX_220 } from "Constants/perfectScrollbarLayout";
import Chart from "react-apexcharts";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import data from "Data/dashboardDevops.json";

function DevopsDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);

  const statusColor = useCallback(status => {
    if (status === "up") return "success";
    if (status === "degraded") return "warning";
    return "danger";
  }, []);

  const deployColor = useCallback(status => {
    if (status === "healthy") return "success";
    if (status === "deploying") return "info";
    return "secondary";
  }, []);

  const severityColor = useCallback(sev => {
    if (sev === "SEV1") return "danger";
    if (sev === "SEV2") return "warning";
    return "secondary";
  }, []);

  const errorChartOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [3, 0], curve: "smooth" },
      colors: [c.ReactifyChartColor5, c.ReactifyChartColor2],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      markers: { size: 0 },
      xaxis: {
        categories: data.errorSpike.categories,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: { style: { colors: c.primaryColor } }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
  }, [colors]);

  const slo = data.slo;

  return (
    <div className="dashboard-vertical dashboard-vertical--devops">
      <Fragment>
        <Row>
          <Colxx xxs="12">
            <BreadcrumbContainer heading={<IntlMessages id="menu.devops" />} />
            <Separator className="mb-5" />
          </Colxx>
        </Row>

        <Row className="mb-2">
          <Colxx md="6" lg="3" className="mb-3">
            <Card className="border-success border-opacity-25 bg-light h-100">
              <CardBody className="py-3">
                <p className="text-muted mb-0 small">
                  <IntlMessages id="devops.slo.target" />
                </p>
                <h3 className="mb-0">{slo.availabilityTarget}%</h3>
              </CardBody>
            </Card>
          </Colxx>
          <Colxx md="6" lg="3" className="mb-3">
            <Card className="border-primary border-opacity-25 bg-light h-100">
              <CardBody className="py-3">
                <p className="text-muted mb-0 small">
                  <IntlMessages id="devops.slo.actual" />
                </p>
                <h3 className="mb-0">{slo.availabilityActual}%</h3>
              </CardBody>
            </Card>
          </Colxx>
          <Colxx md="6" lg="3" className="mb-3">
            <Card className="border-warning border-opacity-25 bg-light h-100">
              <CardBody className="py-3">
                <p className="text-muted mb-0 small">
                  <IntlMessages id="devops.slo.budget" />
                </p>
                <h3 className="mb-0">{slo.errorBudgetRemainingPct}%</h3>
              </CardBody>
            </Card>
          </Colxx>
          <Colxx md="6" lg="3" className="mb-3">
            <Card className="border-info border-opacity-25 bg-light h-100">
              <CardBody className="py-3">
                <p className="text-muted mb-0 small">
                  <IntlMessages id="devops.slo.burn" />
                </p>
                <h3 className="mb-0">{slo.burnRate}x</h3>
              </CardBody>
            </Card>
          </Colxx>
        </Row>

        <Row>
          <Colxx lg="8" className="mb-4">
            <Card className="dashboard-vertical__chart-card">
              <CardBody>
                <CardTitle>
                  <IntlMessages id="devops.chart.errors" />
                </CardTitle>
                <Chart
                  options={errorChartOptions}
                  series={[
                    { name: "5xx / min", data: data.errorSpike.errors },
                    { name: "Deploy marker", data: data.errorSpike.deploys }
                  ]}
                  type="line"
                  height={280}
                  width="100%"
                />
              </CardBody>
            </Card>
          </Colxx>
          <Colxx lg="4" className="mb-4">
            <Card className="h-100">
              <CardBody>
                <CardTitle>
                  <IntlMessages id="devops.section.oncall" />
                </CardTitle>
                <p className="text-muted small mb-3">{data.onCall.rotation}</p>
                <div className="mb-2">
                  <span className="text-muted small d-block">
                    <IntlMessages id="devops.oncall.primary" />
                  </span>
                  <span className="font-weight-medium">
                    {data.onCall.primary}
                  </span>
                </div>
                <div className="mb-3">
                  <span className="text-muted small d-block">
                    <IntlMessages id="devops.oncall.secondary" />
                  </span>
                  <span className="font-weight-medium">
                    {data.onCall.secondary}
                  </span>
                </div>
                <Button color="primary" size="sm" outline block>
                  {data.onCall.escalation}
                </Button>
              </CardBody>
            </Card>
          </Colxx>
        </Row>

        <Row>
          <Colxx lg="6" className="mb-4">
            <Card className="h-100">
              <CardBody>
                <CardTitle>
                  <IntlMessages id="devops.section.services" />
                </CardTitle>
                <Row>
                  {data.services.map((svc, i) => (
                    <Colxx xxs="12" sm="6" key={i} className="mb-2">
                      <div className="border rounded p-3 h-100">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <span className="font-weight-medium">{svc.name}</span>
                          <Badge color={statusColor(svc.status)}>
                            {svc.status}
                          </Badge>
                        </div>
                        <small className="text-muted d-block">{svc.region}</small>
                        <small className="text-muted">
                          {svc.latencyMs}ms p50
                        </small>
                      </div>
                    </Colxx>
                  ))}
                </Row>
              </CardBody>
            </Card>
          </Colxx>
          <Colxx lg="6" className="mb-4">
            <Card className="h-100">
              <CardBody>
                <CardTitle>
                  <IntlMessages id="devops.section.deploys" />
                </CardTitle>
                <ul className="list-unstyled mb-0">
                  {data.deployments.map((d, i) => (
                    <li
                      key={i}
                      className="d-flex justify-content-between align-items-center py-2 border-bottom"
                    >
                      <div>
                        <Badge color="light" className="text-dark me-2">
                          {d.env}
                        </Badge>
                        <code className="small">{d.version}</code>
                        <div className="small text-muted">{d.ago}</div>
                      </div>
                      <Badge color={deployColor(d.status)}>{d.status}</Badge>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </Colxx>
        </Row>

        <Row>
          <Colxx lg="6" className="mb-4">
            <Card className="h-100">
              <CardBody>
                <CardTitle>
                  <IntlMessages id="devops.section.incidents" />
                </CardTitle>
                <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_220}>
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>
                          <IntlMessages id="devops.col.title" />
                        </th>
                        <th>
                          <IntlMessages id="devops.col.severity" />
                        </th>
                        <th>
                          <IntlMessages id="devops.col.state" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.incidents.map(inc => (
                        <tr key={inc.id}>
                          <td>
                            <code className="small">{inc.id}</code>
                          </td>
                          <td>{inc.title}</td>
                          <td>
                            <Badge color={severityColor(inc.severity)}>
                              {inc.severity}
                            </Badge>
                          </td>
                          <td>
                            <span className="small">{inc.state}</span>
                            <div className="text-muted small">{inc.when}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </PerfectScrollbar>
              </CardBody>
            </Card>
          </Colxx>
          <Colxx lg="6" className="mb-4">
            <Card className="h-100">
              <CardBody>
                <CardTitle>
                  <IntlMessages id="devops.section.audit" />
                </CardTitle>
                <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_220}>
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>
                          <IntlMessages id="devops.col.time" />
                        </th>
                        <th>
                          <IntlMessages id="devops.col.who" />
                        </th>
                        <th>
                          <IntlMessages id="devops.col.what" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.auditTail.map((row, i) => (
                        <tr key={i}>
                          <td className="text-muted text-nowrap">{row.t}</td>
                          <td>{row.who}</td>
                          <td>{row.what}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </PerfectScrollbar>
              </CardBody>
            </Card>
          </Colxx>
        </Row>
      </Fragment>
    </div>
  );
}

export default DevopsDashboard;
